import type { WorkspaceTaskRow } from '@/features/tasks';

/**
 * Finished this week (spec 155 US3): the tasks that reached Done in the last
 * seven days, by local day. Pure.
 *
 * The total is the Tasks page's own window, the last 7×24 hours on the time a
 * task finished, so "5 finished this week" opens a list of 5 there. The bars
 * are local days, today and the six before it; a task finished in the hours
 * before the first of those days is counted in the first bar, so the bars
 * always add up to the total.
 */

export const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export interface FinishedDay {
  /** The day's local midnight. */
  start: number;
  count: number;
}

export interface FinishedWeek {
  /** Seven days, oldest first. */
  days: FinishedDay[];
  total: number;
}

function localMidnight(at: number, daysBack: number): number {
  const date = new Date(at);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysBack);
  return date.getTime();
}

export function finishedWeek(
  rows: readonly WorkspaceTaskRow[],
  now: number,
): FinishedWeek {
  const days: FinishedDay[] = [6, 5, 4, 3, 2, 1, 0].map((back) => ({
    start: localMidnight(now, back),
    count: 0,
  }));
  const from = now - WEEK_MS;
  let total = 0;
  for (const row of rows) {
    if (!row.finished || row.finishedAt === null || row.hidden) continue;
    const at = Date.parse(row.finishedAt);
    if (!Number.isFinite(at) || at < from) continue;
    total += 1;
    let index = 0;
    for (let i = days.length - 1; i >= 0; i -= 1) {
      const day = days[i];
      if (day && at >= day.start) {
        index = i;
        break;
      }
    }
    const bucket = days[index];
    if (bucket) bucket.count += 1;
  }
  return { days, total };
}

/** "5 finished this week: 2 today, 3 on Tuesday", newest first. */
export function finishedSentence(week: FinishedWeek, now: number): string {
  if (week.total === 0) return 'Nothing finished this week';
  const today = localMidnight(now, 0);
  const yesterday = localMidnight(now, 1);
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'long' });
  const parts = [...week.days]
    .reverse()
    .filter((day) => day.count > 0)
    .map((day) => {
      const when =
        day.start === today
          ? 'today'
          : day.start === yesterday
            ? 'yesterday'
            : `on ${weekday.format(day.start)}`;
      return `${String(day.count)} ${when}`;
    });
  return `${String(week.total)} finished this week: ${parts.join(', ')}`;
}
